import React, { Fragment, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { ReviewView } from './components/ReviewView'
import HowItWorksView from './components/HowItWorksView'
import ThankYouView from './components/ThankYouView'
import StatusView from './components/StatusView'
import UploadView from './components/UploadView'
import { SetReminderWrapper } from './components/SetReminderWrapper'
import UploadedImageList from './components/UploadedImageList'
import {
  submitReceiptSubmission,
  requestEmailSMSReminder,
} from 'services/receipt'
import { scrollToTopOfCurrentStep } from 'services/break'
import analytics from 'global/AnalyticsData'
import './ReceiptVerificationStep.scss'
import './ReceiptVerificationStepMobile.scss'
import { useUploadImages } from './hooks/useUploadImages'
import { useSubmissionStatus } from './hooks/useSubmissionStatus'

const ReceiptVerificationStep = ({
  brk,
  breakId,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  skipButton,
  nextStepButton,
  themeColors,
  handleInstantWinEvent = null,
  isEmbed = false,
}) => {
  const [position, setPosition] = useState(0)
  const [rewardSelected, setRewardSelected] = useState(null)
  const [reminderFormSent, setReminderFormSent] = useState(false)
  const [nonFieldErrors, setNonFieldErrors] = useState([])

  const [sizeError, setSizeError] = useState(false)
  const [typeError, setTypeError] = useState(false)
  const [amountError, setAmountError] = useState(false)
  const handleUploadError = (sizeError, typeError, amountError) => {
    setSizeError(sizeError)
    setTypeError(typeError)
    setAmountError(amountError)
  }

  const { previousSubmissionStatus, loaded } = useSubmissionStatus(
    breakId,
    step,
    handleInstantWinEvent,
  )
  const {
    uploadProgress,
    uploadedImages,
    uploadErrors,
    handleImageUpload: handleImageUploadProp,
    handleRemoveImage,
    setUploadErrors,
    receiptSubmissionId,
  } = useUploadImages(breakId, step)

  const animationDuration = 400
  const stepData = step.receiptVerificationCampaign

  const handleNotYetClicked = () => {
    scrollToTopOfCurrentStep(step.id)
    setPosition(4)
  }

  const handleUploadNext = () => {
    let currentPosition = position
    scrollToTopOfCurrentStep(step.id)
    setPosition(currentPosition + 1)
  }

  const editMySubmission = () => {
    scrollToTopOfCurrentStep(step.id)
    setPosition(position - 1)
  }

  const handleTheFormSubmission = async values => {
    await analytics.stepEventNewUtm('step_interaction_receipt_submit', step)
    const rewardSelectedLocal = stepData.rewards.find(
      reward => reward.id === values.rewardId,
    )
    const submissionObject = {
      breakId: breakId,
      stepId: step.id,
      receiptId: step.receiptVerificationCampaign.id,
      receiptSubmissionId: receiptSubmissionId,
      ...values,
    }
    const response = await submitReceiptSubmission(submissionObject)
    if (response.status === 200) {
      setRewardSelected(rewardSelectedLocal)
      scrollToTopOfCurrentStep(step.id)
      setPosition(position + 1)
      onEngage()
      onAwardPoints()
    } else {
      let result = await response.json()
      setNonFieldErrors([result.message])
    }
  }

  //this is a wrapper function for the original function due to instant win.
  const handleFormSubmission = async values => {
    handleTheFormSubmission(values)
  }

  const handleImageUpload = async files => {
    if (files.length > 0) {
      scrollToTopOfCurrentStep(step.id)
      setPosition(1)
      handleImageUploadProp(files)
    }
  }

  const handleRequestReminder = async (
    email,
    phoneNumber,
    type,
    turnstileToken,
  ) => {
    analytics.stepEvent('step_interaction_receipt_reminder_submit', step)
    if (window !== undefined) {
      try {
        const link = window.location.href
        const resp = await requestEmailSMSReminder(
          breakId,
          step.id,
          step.receiptVerificationCampaign.id,
          email,
          phoneNumber,
          type,
          link,
          turnstileToken,
        )
        resp.status === 200
          ? setReminderFormSent(true)
          : setReminderFormSent(false)

        setReminderFormSent(true)
        onEngage()
        onAwardPoints()
      } catch (e) {
        console.log(`issue submitting reminder form: ${e}`)
      }
    }
  }
  const handleStatusRetrieved = async status => {
    if (status === 'approved') {
      //This ask is weird so it deserves a weird solution
      analytics.stepEvent('step_interaction_receipt_approval', step)
    }
  }
  const hasUploadedImages = uploadedImages.length > 0
  const stepViewArray = [
    <HowItWorksView
      themeColors={themeColors}
      handleImageUpload={handleImageUpload}
      headerImage={stepData.headerImage}
      headline={stepData.headline}
      introCopy={stepData.introCopy}
      userPathHeadline={stepData.userPathHeadline}
      instructions={stepData.instructions}
      onNotYetClicked={handleNotYetClicked}
      handleUploadError={handleUploadError}
      sizeError={sizeError}
      typeError={typeError}
      amountError={amountError}
      enableReminder={stepData.enableReminder}
      isEmbed={isEmbed}
    />,
    <UploadView
      canContinue={hasUploadedImages}
      setUploadErrors={setUploadErrors}
      onUploadNext={handleUploadNext}
      handleImageUpload={handleImageUpload}
      headline={stepData.uploadHeadline}
      introCopy={stepData.uploadIntroCopy}
      uploadInProgress={uploadProgress}
      handleUploadError={handleUploadError}
      handleUpload={() => {
        setSizeError(false)
        setTypeError(false)
        setAmountError(false)
      }}
      sizeError={sizeError}
      typeError={typeError}
      amountError={amountError}
    >
      <UploadedImageList
        uploadErrors={uploadErrors}
        uploadedImages={uploadedImages}
        handleRemoveImage={handleRemoveImage}
        uploadProgress={uploadProgress}
      />
    </UploadView>,
    <ReviewView
      headerTitle={stepData.reviewHeader}
      headerSubtitle={stepData.reviewCopy}
      breakPermalink={brk.permalink}
      stepId={stepData.id}
      stepType={step.type}
      reviewListHeader={stepData.reviewListHeader}
      instructions={stepData.detailedInstructions}
      rewardOptions={stepData.rewards}
      onFormSubmission={handleFormSubmission}
      editMySubmission={editMySubmission}
      nonFieldErrors={nonFieldErrors}
      communicationChannel={stepData.communicationChannelAvailable}
      isEmbed={isEmbed}
    >
      <UploadedImageList
        uploadErrors={uploadErrors}
        uploadedImages={uploadedImages}
        handleRemoveImage={handleRemoveImage}
        uploadProgress={uploadProgress}
      />
    </ReviewView>,
    <>
      {rewardSelected && (
        <ThankYouView
          headline={rewardSelected.thankyouHeader}
          bodyCopy={rewardSelected.thankyouBody}
          image={rewardSelected.rewardImage}
          dropdownButtons={rewardSelected.dropdownButtons}
          buttonTextColor={brk.themeColor1}
        />
      )}{' '}
    </>,
    <SetReminderWrapper
      communicationChannel={stepData.communicationChannelAvailable}
      setPosition={setPosition}
      requestReminder={handleRequestReminder}
      reminderFormSent={reminderFormSent}
      reminderHeader={stepData.reminderHeader}
      reminderCopy={stepData.reminderCopy}
      reminderThanksHeader={stepData.reminderThanksHeader}
      reminderThanksCopy={stepData.reminderThanksCopy}
      headerImage={stepData.headerImage}
      stepType={step.type}
      stepId={stepData.id}
      breakPermalink={brk.permalink}
      instructionsHeader={stepData.reminderThanksInstructionsHeader}
      instructions={stepData.reminderInstructions}
      reminderConfirmationCopy={stepData.reminderConfirmationCopy}
      reminderConfirmationCopySms={stepData.reminderConfirmationCopySms}
    />,
  ]

  return (
    <>
      <section className="receipt-verification-step">
        {loaded === true && (
          <>
            <div
              className={`step-content with-shadow ${
                brk?.navigationType === 'TOP' ? 'top-navigation' : ''
              }`}
            >
              <div className="step-view-container will-fade-transition-container">
                {previousSubmissionStatus === null ? (
                  <>
                    {stepViewArray.map((component, i) => {
                      return (
                        <CSSTransition
                          key={i}
                          in={position === i}
                          timeout={animationDuration}
                          className={`will-fade-transition ${
                            position === i ? 'is-active' : 'is-inactive'
                          }`}
                        >
                          <div>
                            {position === i && (
                              <Fragment
                                key={`receipt-transition-view-item-${i}`}
                              >
                                {component}
                              </Fragment>
                            )}
                          </div>
                        </CSSTransition>
                      )
                    })}
                  </>
                ) : (
                  <StatusView
                    status={previousSubmissionStatus.status}
                    reward={previousSubmissionStatus.reward}
                    onStatusRetrieved={handleStatusRetrieved}
                  />
                )}
              </div>
              {stepData.stepTerms && (
                <div className={`legal-text-container`}>
                  <div className="step-terms-copy" dangerouslySetInnerHTML={{ __html: stepData.stepTerms }} />
                </div>
              )}

              {skipButton}
              {nextStepButton}
            </div>
          </>
        )}
      </section>
    </>
  )
}

export default ReceiptVerificationStep
